import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Calendar,
  CheckCircle,
  FileText,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBlog = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image: "",
    category: "",
    tags: "",
    status: "draft",
    author_name: "",
    meta_title: "",
    meta_description: "",
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/admin/blog/posts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === "title" && !editingPost) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt || "",
        content: post.content || "",
        featured_image: post.featured_image || "",
        category: post.category || "",
        tags: post.tags?.join(", ") || "",
        status: post.status,
        author_name: post.author_name || "",
        meta_title: post.meta_title || "",
        meta_description: post.meta_description || "",
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: "",
        slug: "",
        excerpt: "",
        content: "",
        featured_image: "",
        category: "",
        tags: "",
        status: "draft",
        author_name: "",
        meta_title: "",
        meta_description: "",
      });
    }
    setShowEditor(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.slug || !formData.content) {
      toast.error("Please fill in title, slug, and content");
      return;
    }

    setSaving(true);

    try {
      const params = new URLSearchParams({
        title: formData.title,
        slug: formData.slug,
        excerpt: formData.excerpt,
        content: formData.content,
        featured_image: formData.featured_image,
        category: formData.category,
        tags: formData.tags,
        status: formData.status,
        author_name: formData.author_name,
        meta_title: formData.meta_title,
        meta_description: formData.meta_description,
      });

      const url = editingPost
        ? `${API}/admin/blog/posts/${editingPost.post_id}?${params.toString()}`
        : `${API}/admin/blog/posts?${params.toString()}`;

      const response = await fetch(url, {
        method: editingPost ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save post");
      }

      toast.success(editingPost ? "Post updated!" : "Post created!");
      setShowEditor(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!confirm("Are you sure you want to delete this post?")) return;

    try {
      const response = await fetch(`${API}/admin/blog/posts/${postId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to delete post");

      toast.success("Post deleted!");
      fetchPosts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "published") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Published
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
        <FileText className="w-3 h-3" />
        Draft
      </span>
    );
  };

  const categories = [
    "Healthcare Startups",
    "Practice Management",
    "Team Leadership",
    "Patient Experience",
    "Compliance & Regulations",
    "Technology",
    "Marketing",
    "Industry News",
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy tracking-tight">Blog Manager</h1>
            <p className="text-sm text-gray-400 mt-1">Create and manage blog posts</p>
          </div>
          <Button onClick={() => openEditor()} className="bg-rnb-navy hover:bg-rnb-navy/90 gap-1.5 text-xs h-9" size="sm" data-testid="create-post-btn">
            <Plus className="h-3.5 w-3.5" />
            New Post
          </Button>
        </div>

        <div className="space-y-6">
            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rnb-navy">{posts.length}</p>
                      <p className="text-sm text-rnb-gray">Total Posts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rnb-navy">
                        {posts.filter((p) => p.status === "published").length}
                      </p>
                      <p className="text-sm text-rnb-gray">Published</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rnb-navy">
                        {posts.filter((p) => p.status === "draft").length}
                      </p>
                      <p className="text-sm text-rnb-gray">Drafts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Posts Table */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>All Posts</CardTitle>
              </CardHeader>
              <CardContent>
                {posts.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-rnb-gray/30 mx-auto mb-4" />
                    <p className="text-rnb-gray">No blog posts yet</p>
                    <p className="text-sm text-rnb-gray/70 mb-4">Create your first post to get started</p>
                    <Button onClick={() => openEditor()}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Post
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {posts.map((post) => (
                        <TableRow key={post.post_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-rnb-navy">{post.title}</p>
                              <p className="text-xs text-rnb-gray">/blog/{post.slug}</p>
                            </div>
                          </TableCell>
                          <TableCell>{post.category || "—"}</TableCell>
                          <TableCell>{getStatusBadge(post.status)}</TableCell>
                          <TableCell className="text-sm text-rnb-gray">
                            {new Date(post.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {post.status === "published" && (
                                <Link to={`/blog/${post.slug}`} target="_blank">
                                  <Button variant="ghost" size="sm">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </Link>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => openEditor(post)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(post.post_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? "Edit Post" : "Create New Post"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="Enter post title"
                  data-testid="post-title"
                />
              </div>
              <div className="space-y-2">
                <Label>URL Slug *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  placeholder="url-friendly-slug"
                  data-testid="post-slug"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Excerpt</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => handleChange("excerpt", e.target.value)}
                placeholder="Brief summary of the post (shown in previews)"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Content * (HTML supported)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => handleChange("content", e.target.value)}
                placeholder="Write your post content here. HTML tags are supported for formatting."
                rows={12}
                className="font-mono text-sm"
                data-testid="post-content"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Featured Image URL</Label>
                <Input
                  value={formData.featured_image}
                  onChange={(e) => handleChange("featured_image", e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => handleChange("category", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={formData.tags}
                  onChange={(e) => handleChange("tags", e.target.value)}
                  placeholder="healthcare, startup, tips"
                />
              </div>
              <div className="space-y-2">
                <Label>Author Name</Label>
                <Input
                  value={formData.author_name}
                  onChange={(e) => handleChange("author_name", e.target.value)}
                  placeholder="Leave blank to use your name"
                  data-testid="post-author"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange("status", value)}
                >
                  <SelectTrigger data-testid="post-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* SEO Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-rnb-navy mb-3">SEO Settings</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Meta Title</Label>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => handleChange("meta_title", e.target.value)}
                    placeholder="Leave blank to use post title"
                    data-testid="post-meta-title"
                  />
                  <p className="text-xs text-rnb-gray">
                    {formData.meta_title?.length || 0}/60 characters recommended
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Meta Description</Label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => handleChange("meta_description", e.target.value)}
                    placeholder="Leave blank to auto-generate from excerpt"
                    rows={2}
                    data-testid="post-meta-description"
                  />
                  <p className="text-xs text-rnb-gray">
                    {formData.meta_description?.length || 0}/160 characters recommended
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={saving} data-testid="save-post-btn">
                {saving ? "Saving..." : editingPost ? "Update Post" : "Create Post"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlog;
